// client/src/hooks/useWatchdog.js
// React hooks for the Watchdog event-driven pipeline.

import { useState, useEffect, useCallback } from "react";
import {
    fetchWatchdogStats,
    fetchWatchdogOrders,
    fetchWatchdogActivity,
    fetchWatchdogConfig,
    saveWatchdogConfig,
    executeWatchdogOrder,
    dismissWatchdogOrder,
    revalidateWatchdogOrder,
    clearCompletedWatchdogOrders,
    bulkDismissWatchdogOrders,
} from "../services/api";

// ─── Stats Hook (polls every 5s) ────────────────────────────────────────────
export function useWatchdogStats() {
    const [stats, setStats] = useState(null);
    const [processor, setProcessor] = useState(null);
    const [lastUpdated, setLastUpdated] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const data = await fetchWatchdogStats();
            setStats(data.stats);
            setProcessor(data.processor || null);
            setLastUpdated(data.timestamp || new Date().toISOString());
            setError(null);
        } catch (err) {
            setError(err.message || "Failed to fetch stats");
            console.error("Watchdog stats error:", err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 5000);
        return () => clearInterval(interval);
    }, [refresh]);

    return { stats, processor, lastUpdated, loading, error, refresh };
}

// ─── Orders Hook (paginated with filters) ───────────────────────────────────
export function useWatchdogOrders(initialFilters = {}) {
    const [orders, setOrders] = useState([]);
    const [total, setTotal] = useState(0);
    const [pages, setPages] = useState(0);
    const [page, setPage] = useState(1);
    const [filters, setFilters] = useState({
        status: "all",
        search: "",
        limit: 50,
        ...initialFilters,
    });
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            setLoading(true);
            const data = await fetchWatchdogOrders({ ...filters, page });
            setOrders(data.orders || []);
            setTotal(data.total || 0);
            setPages(data.pages || 0);
            setError(null);
        } catch (err) {
            setError(err.message || "Failed to fetch orders");
            console.error("Watchdog orders error:", err.message);
        } finally {
            setLoading(false);
        }
    }, [filters, page]);

    useEffect(() => { refresh(); }, [refresh]);

    // Auto-refresh every 10s
    useEffect(() => {
        const interval = setInterval(refresh, 10000);
        return () => clearInterval(interval);
    }, [refresh]);

    return { orders, total, pages, page, setPage, filters, setFilters, loading, error, refresh };
}

// ─── Activity Hook (polls every 5s) ─────────────────────────────────────────
export function useWatchdogActivity(limit = 30, filter = "all") {
    const [activity, setActivity] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const data = await fetchWatchdogActivity({ limit, filter });
            setActivity(data.activity || []);
            setError(null);
        } catch (err) {
            setError(err.message || "Failed to fetch activity");
            console.error("Watchdog activity error:", err.message);
        } finally {
            setLoading(false);
        }
    }, [limit, filter]);

    useEffect(() => {
        refresh();
        const interval = setInterval(refresh, 5000);
        return () => clearInterval(interval);
    }, [refresh]);

    return { activity, loading, error, refresh };
}

// ─── Config Hook ─────────────────────────────────────────────────────────────
export function useWatchdogConfig() {
    const [config, setConfig] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const [saveError, setSaveError] = useState(null);

    const load = useCallback(async () => {
        try {
            const data = await fetchWatchdogConfig();
            setConfig(data.config);
            setError(null);
        } catch (err) {
            setError(err.message || "Failed to load config");
            console.error("Watchdog config error:", err.message);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const save = useCallback(async (updates) => {
        setSaving(true);
        setSaveError(null);
        try {
            const data = await saveWatchdogConfig(updates);
            setConfig(data.config);
            return data.config;
        } catch (err) {
            const msg = err.message || "Failed to save config";
            setSaveError(msg);
            console.error("Watchdog config save error:", msg);
            throw err;
        } finally {
            setSaving(false);
        }
    }, []);

    return { config, loading, saving, save, reload: load, error, saveError, clearSaveError: () => setSaveError(null) };
}

// ─── Order Actions Hook ─────────────────────────────────────────────────────
export function useWatchdogActions(refreshOrders) {
    const [actionLoading, setActionLoading] = useState(null); // orderId being actioned
    const [actionError, setActionError] = useState(null);     // { orderId, message, action }
    const [actionResult, setActionResult] = useState(null);   // { orderId, message, action }

    const clearActionError = useCallback(() => setActionError(null), []);
    const clearActionResult = useCallback(() => setActionResult(null), []);

    const execute = useCallback(async (orderId) => {
        setActionLoading(orderId);
        setActionError(null);
        try {
            const data = await executeWatchdogOrder(orderId);
            setActionResult({
                orderId,
                message: `Order ${data.order?.order_number || orderId} — action executed successfully`,
                action: "execute",
            });
            if (refreshOrders) refreshOrders();
        } catch (err) {
            const msg = err.message || "Execute failed";
            setActionError({ orderId, message: msg, action: "execute" });
            console.error("Execute error:", msg);
        } finally {
            setActionLoading(null);
        }
    }, [refreshOrders]);

    const dismiss = useCallback(async (orderId) => {
        setActionLoading(orderId);
        setActionError(null);
        try {
            await dismissWatchdogOrder(orderId);
            setActionResult({
                orderId,
                message: `Order dismissed successfully`,
                action: "dismiss",
            });
            if (refreshOrders) refreshOrders();
        } catch (err) {
            const msg = err.message || "Dismiss failed";
            setActionError({ orderId, message: msg, action: "dismiss" });
            console.error("Dismiss error:", msg);
        } finally {
            setActionLoading(null);
        }
    }, [refreshOrders]);

    const revalidate = useCallback(async (orderId) => {
        setActionLoading(orderId);
        setActionError(null);
        try {
            await revalidateWatchdogOrder(orderId);
            setActionResult({
                orderId,
                message: `Order queued for re-validation`,
                action: "revalidate",
            });
            if (refreshOrders) refreshOrders();
        } catch (err) {
            const msg = err.message || "Revalidate failed";
            setActionError({ orderId, message: msg, action: "revalidate" });
            console.error("Revalidate error:", msg);
        } finally {
            setActionLoading(null);
        }
    }, [refreshOrders]);

    const clearCompleted = useCallback(async () => {
        setActionLoading("clear-completed");
        setActionError(null);
        try {
            const data = await clearCompletedWatchdogOrders();
            setActionResult({
                orderId: null,
                message: data.message || `Archived ${data.archived ?? data.deleted} completed order(s)`,
                action: "clearCompleted",
            });
            if (refreshOrders) refreshOrders();
        } catch (err) {
            const msg = err.message || "Clear completed failed";
            setActionError({ orderId: null, message: msg, action: "clearCompleted" });
            console.error("Clear completed error:", msg);
        } finally {
            setActionLoading(null);
        }
    }, [refreshOrders]);

    const bulkDismiss = useCallback(async (ids) => {
        if (!ids || ids.length === 0) return;
        setActionLoading("bulk-dismiss");
        setActionError(null);
        try {
            const data = await bulkDismissWatchdogOrders(ids);
            setActionResult({
                orderId: null,
                message: data.message || `Dismissed ${data.dismissed} order(s)`,
                action: "bulkDismiss",
            });
            if (refreshOrders) refreshOrders();
        } catch (err) {
            const msg = err.message || "Bulk dismiss failed";
            setActionError({ orderId: null, message: msg, action: "bulkDismiss" });
            console.error("Bulk dismiss error:", msg);
        } finally {
            setActionLoading(null);
        }
    }, [refreshOrders]);

    return {
        execute, dismiss, revalidate, clearCompleted, bulkDismiss, actionLoading,
        actionError, actionResult,
        clearActionError, clearActionResult,
    };
}
